import { z } from 'zod';
import { zCanal, zColor, zDinero, zEmail, zEstado, zId, zIdioma, zPaginacion, zRuc, zTelefono } from './comunes.js';
// ══════════ Marcas ══════════
export const zMarcaAdmin = z.object({
    id: zId,
    codigoExterno: z.string(),
    nombre: z.string(),
    slug: z.string(),
    colorPrimario: z.string().nullable(),
    colorSecundario: z.string().nullable(),
    logoAssetId: z.string().nullable(),
    logoUrl: z.string().nullable(),
    /** true = el logo es SVG y se puede recolorear. */
    logoEsSvg: z.boolean(),
    estado: zEstado,
});
export const zCrearMarca = z.object({
    codigoExterno: z.string().min(1).max(40),
    nombre: z.string().min(1).max(120),
    colorPrimario: zColor.optional(),
    colorSecundario: zColor.optional(),
    logoAssetId: zId.optional(),
});
export const zEditarMarca = zCrearMarca.partial().extend({ estado: zEstado.optional() });
// ══════════ Productos ══════════
export const zProductoAdmin = z.object({
    id: zId,
    marcaId: zId,
    marcaNombre: z.string(),
    referencia: z.string(),
    nombre: z.string(),
    categoria: z.string(),
    tipo: z.string().nullable(),
    coleccion: z.string().nullable(),
    linea: z.string().nullable(),
    familia: z.string().nullable(),
    color: z.string().nullable(),
    pvpSugerido: z.number().nullable(),
    imagenUrl: z.string().nullable(),
    tieneImagen: z.boolean(),
    fechaExpiracion: z.string().nullable(),
    estado: zEstado,
});
export const zCrearProducto = z.object({
    marcaId: zId,
    referencia: z.string().min(1).max(60),
    nombre: z.string().min(1).max(200),
    categoria: z.string().min(1).max(80),
    tipo: z.string().max(80).optional(),
    coleccion: z.string().max(80).optional(),
    linea: z.string().max(80).optional(),
    familia: z.string().max(80).optional(),
    color: z.string().max(80).optional(),
    pvpSugerido: zDinero.optional(),
    imagenAssetId: zId.optional(),
    fechaExpiracion: z.string().datetime().optional(),
});
export const zEditarProducto = zCrearProducto.partial().extend({ estado: zEstado.optional() });
export const zFiltroProductosAdmin = zPaginacion.extend({
    marca: zId.optional(),
    categoria: z.string().optional(),
    q: z.string().optional(),
    sinImagen: z.coerce.boolean().optional(),
});
// ══════════ Clientes ══════════
export const zClienteAdmin = z.object({
    id: zId,
    codigoExterno: z.string(),
    razonSocial: z.string(),
    nombreNegocio: z.string().nullable(),
    ruc: z.string(),
    ciudad: z.string().nullable(),
    region: z.string().nullable(),
    email: z.string().nullable(),
    telefono: z.string().nullable(),
    categoriaId: zId.nullable(),
    categoriaNombre: z.string().nullable(),
    tipoId: zId.nullable(),
    tipoNombre: z.string().nullable(),
    contactoNombre: z.string().nullable(),
    contactoTelefono: z.string().nullable(),
    estado: zEstado,
    /** Cuántas referencias ve el almacén y cuándo fue su última compra. */
    numProductos: z.number(),
    /** Cuántas de sus referencias están SIN foto (invisibles para él). */
    sinFoto: z.number(),
    ultimoPedido: z.string().nullable(),
    usuarios: z.array(z.object({
        id: zId,
        username: z.string(),
        email: z.string().nullable(),
        rol: z.string(),
        estado: zEstado,
        idioma: zIdioma,
        ultimoAcceso: z.string().nullable(),
    })),
});
export const zCrearCliente = z.object({
    codigoExterno: z.string().min(1).max(40),
    razonSocial: z.string().min(1).max(200),
    nombreNegocio: z.string().max(200).optional(),
    ruc: zRuc,
    ciudad: z.string().max(80).optional(),
    region: z.string().max(40).optional(),
    email: zEmail.optional(),
    telefono: zTelefono.optional(),
    categoriaId: zId.nullable().optional(),
    tipoId: zId.nullable().optional(),
    contactoNombre: z.string().max(120).nullable().optional(),
    contactoTelefono: zTelefono.nullable().optional(),
});
export const zEditarCliente = zCrearCliente.partial().extend({ estado: zEstado.optional() });
export const zCrearUsuarioCliente = z.object({
    rol: z.enum(['CLIENTE_ADMIN', 'CLIENTE_VENDEDOR']),
    username: z.string().min(3).max(120),
    email: z.string().email().optional(),
    idioma: zIdioma.optional(),
});
/** La clave generada se muestra UNA vez; en la base sólo vive el hash. */
export const zCredencialGenerada = z.object({
    usuarioId: zId,
    username: z.string(),
    password: z.string(),
});
// ══════════ Importaciones ══════════
export const zTipoImport = z.enum(['PRODUCTOS', 'CLIENTES', 'PEDIDOS', 'MATERIALES']);
export const zModoImport = z.enum(['DRY_RUN', 'APPLY']);
export const zEstadoJob = z.enum(['PENDIENTE', 'PROCESANDO', 'LISTO', 'ERROR']);
export const zErrorImport = z.object({
    fila: z.number(),
    referencia: z.string().optional(),
    motivo: z.string(),
});
export const zResumenImport = z.object({
    totalFilas: z.number(),
    creados: z.number(),
    actualizados: z.number(),
    errores: z.number(),
    /** Productos que quedaron sin imagen y por tanto invisibles (D41). */
    sinImagen: z.array(z.string()).default([]),
    /** Sólo importación de productos: marcas que no existían y se crean al vuelo. */
    marcasNuevas: z.array(z.string()).default([]),
    /** Sólo importación de clientes: cuántas credenciales nuevas se generaron. */
    credencialesGeneradas: z.number().optional(),
});
export const zImportJobResp = z.object({
    id: zId,
    tipo: zTipoImport,
    modo: zModoImport,
    estado: zEstadoJob,
    resumen: zResumenImport.nullable(),
    errores: z.array(zErrorImport).nullable(),
    createdAt: z.string(),
    finishedAt: z.string().nullable(),
});
// ══════════ Branding ══════════
export const zBrandingAdmin = z.object({
    nombreVisible: z.string().max(80).nullable(),
    logoAssetId: z.string().nullable(),
    logoOscuroAssetId: z.string().nullable(),
    faviconAssetId: z.string().nullable(),
    colorPrimario: zColor,
    colorAcento: zColor,
    colorFondo: zColor,
    modoOscuro: z.boolean(),
    idiomaPorDefecto: zIdioma,
});
// ══════════ Fuentes ══════════
export const zFuente = z.object({
    id: zId,
    familia: z.string(),
    peso: z.number(),
    italic: z.boolean(),
    origen: z.enum(['SUBIDA', 'GOOGLE']),
    url: z.string().nullable(),
    licenciaConfirmada: z.boolean(),
});
export const zCrearFuente = z.object({
    familia: z.string().min(1).max(80),
    peso: z.number().int().min(100).max(900).default(400),
    italic: z.boolean().default(false),
    origen: z.enum(['SUBIDA', 'GOOGLE']),
    assetId: zId.optional(),
    googleSlug: z.string().max(80).optional(),
    licenciaConfirmada: z.boolean().default(false),
});
// ══════════ Media ══════════
export const zAssetSubido = z.object({
    assetId: zId,
    url: z.string(),
    mime: z.string(),
    bytes: z.number(),
    width: z.number().nullable(),
    height: z.number().nullable(),
});
export const zContactoPerfil = z.object({
    id: zId,
    canal: zCanal,
    valor: z.string(),
    etiqueta: z.string().nullable(),
    principal: z.boolean(),
});
//# sourceMappingURL=admin.js.map